import { parseFramerProjectKey } from '@detachly/contracts'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, CodeInline, ErrorPanel } from '../components/ui'
import { useApi } from '../lib/api-context'
import { fieldErrors } from '../lib/errors'

const STEPS = ['Project', 'Access', 'Scan'] as const
type Step = 0 | 1 | 2

type Draft = {
  name: string
  framerUrl: string
  apiKey: string
  ownership: boolean
}

const EMPTY: Draft = { name: '', framerUrl: '', apiKey: '', ownership: false }

/** Client-side checks mirror the server's, so the user is not told "invalid" after a round trip. */
function validate(step: Step, draft: Draft): Record<string, string> {
  const errors: Record<string, string> = {}

  if (step === 0) {
    if (draft.name.trim().length === 0) errors.name = 'Give this project a name.'
    if (draft.name.trim().length > 60) errors.name = 'Keep the name under 60 characters.'
    if (parseFramerProjectKey(draft.framerUrl) === null) {
      errors.framerUrl = 'Paste the Framer project URL (framer.com/projects/…) or the project id.'
    }
  }

  if (step === 1) {
    // The `fr_` prefix is a hint, not a rule: Framer changing its token format must not lock
    // anyone out. The real check is the connection test the server runs on submit.
    if (draft.apiKey.trim().length < 8) errors.apiKey = 'Paste the API key from Framer.'
    if (!draft.ownership) {
      errors.ownership = 'Confirm you own or are authorized to convert this project.'
    }
  }

  return errors
}

export function ConnectProjectPage() {
  const api = useApi()
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>(0)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showKey, setShowKey] = useState(false)
  const [submitError, setSubmitError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))

  const goNext = () => {
    const found = validate(step, draft)
    setErrors(found)
    if (Object.keys(found).length === 0) setStep((current) => (current + 1) as Step)
  }

  const submit = async () => {
    setBusy(true)
    setSubmitError(null)
    try {
      // Connecting also spends one Framer connection test server-side; a bad key never gets stored.
      const project = await api.connectProject({
        name: draft.name.trim(),
        framerUrl: draft.framerUrl.trim(),
        apiKey: draft.apiKey.trim(),
        ownership: true,
      })
      const job = await api.startJob(project.id, 'scan')
      navigate(`/app/jobs/${job.jobId}`)
    } catch (error: unknown) {
      setSubmitError(error)
      setErrors(fieldErrors(error))
      setBusy(false)
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Connect a Framer project</h1>
          <p className="muted">
            Detachly reads your project through Framer&rsquo;s own API, with a key you create and
            can revoke.
          </p>
        </div>
        <Link to="/app/projects" className="button button--secondary">
          Cancel
        </Link>
      </div>

      <ol className="stepper">
        {STEPS.map((label, index) => (
          <li
            key={label}
            className={[
              'stepper__item',
              index === step ? 'stepper__item--current' : '',
              index < step ? 'stepper__item--done' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-current={index === step ? 'step' : undefined}
          >
            <span className="mono">{index + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      <div className="wizard">
        <Card>
          {step === 0 ? (
            <div className="stack">
              <div className="field">
                <label className="field__label" htmlFor="name">
                  Project name
                </label>
                <input
                  id="name"
                  className="input"
                  value={draft.name}
                  onChange={(event) => set({ name: event.target.value })}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  autoComplete="off"
                />
                {errors.name ? (
                  <p className="field__error" id="name-error">
                    {errors.name}
                  </p>
                ) : null}
              </div>

              <div className="field">
                <label className="field__label" htmlFor="framerUrl">
                  Framer project URL
                </label>
                <input
                  id="framerUrl"
                  className="input input--mono"
                  placeholder="https://framer.com/projects/My-site--aabbccdd1122eeff3344"
                  value={draft.framerUrl}
                  onChange={(event) => set({ framerUrl: event.target.value })}
                  aria-invalid={Boolean(errors.framerUrl)}
                  aria-describedby={errors.framerUrl ? 'framerUrl-error' : 'framerUrl-hint'}
                  autoComplete="off"
                />
                <p className="field__hint" id="framerUrl-hint">
                  Open your project in Framer and copy the address bar.
                </p>
                {errors.framerUrl ? (
                  <p className="field__error" id="framerUrl-error">
                    {errors.framerUrl}
                  </p>
                ) : null}
              </div>

              <Button onClick={goNext}>Continue</Button>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="stack">
              <div className="field">
                <label className="field__label" htmlFor="apiKey">
                  Framer API key
                </label>
                <div className="input-group">
                  <input
                    id="apiKey"
                    className="input input--mono"
                    type={showKey ? 'text' : 'password'}
                    value={draft.apiKey}
                    onChange={(event) => set({ apiKey: event.target.value })}
                    aria-invalid={Boolean(errors.apiKey)}
                    aria-describedby={errors.apiKey ? 'apiKey-error' : 'apiKey-hint'}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button variant="secondary" onClick={() => setShowKey((value) => !value)}>
                    {showKey ? 'Hide' : 'Show'}
                  </Button>
                </div>
                <p className="field__hint" id="apiKey-hint">
                  Stored encrypted, used only to read your project, and never shown again.
                </p>
                {errors.apiKey ? (
                  <p className="field__error" id="apiKey-error">
                    {errors.apiKey}
                  </p>
                ) : null}
              </div>

              <div className="field">
                <label className="checkbox" htmlFor="ownership">
                  <input
                    id="ownership"
                    type="checkbox"
                    checked={draft.ownership}
                    onChange={(event) => set({ ownership: event.target.checked })}
                    aria-invalid={Boolean(errors.ownership)}
                    aria-describedby={errors.ownership ? 'ownership-error' : undefined}
                  />
                  <span>
                    I own this Framer project, or I am authorized by its owner to convert it.
                    Detachly exports content and rebuilds code for projects you have the right to.
                  </span>
                </label>
                {errors.ownership ? (
                  <p className="field__error" id="ownership-error">
                    {errors.ownership}
                  </p>
                ) : null}
              </div>

              <div className="row">
                <Button variant="secondary" onClick={() => setStep(0)}>
                  Back
                </Button>
                <Button onClick={goNext}>Continue</Button>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="stack">
              <div className="stack stack--tight">
                <h2>Ready to scan</h2>
                <p className="muted">
                  We will test the key against Framer, save it encrypted, and run a first scan. The
                  scan only reads — it changes nothing in your Framer project.
                </p>
              </div>

              <dl className="stack stack--tight">
                <div className="row">
                  <dt className="subtle">Name</dt>
                  <dd>{draft.name}</dd>
                </div>
                <div className="row">
                  <dt className="subtle">Project</dt>
                  <dd>
                    <CodeInline>{parseFramerProjectKey(draft.framerUrl)}</CodeInline>
                  </dd>
                </div>
                <div className="row">
                  <dt className="subtle">Key</dt>
                  <dd>
                    <CodeInline>{`fr_…${draft.apiKey.slice(-4)}`}</CodeInline>
                  </dd>
                </div>
              </dl>

              {submitError ? <ErrorPanel error={submitError} /> : null}

              <div className="row">
                <Button variant="secondary" onClick={() => setStep(1)} disabled={busy}>
                  Back
                </Button>
                <Button onClick={() => void submit()} disabled={busy}>
                  {busy ? 'Connecting…' : 'Connect and scan'}
                </Button>
              </div>
            </div>
          ) : null}
        </Card>

        <Card flat>
          <div className="card__title">
            <h2>Where to find the key</h2>
          </div>
          <ol className="help-list">
            <li>Open your project in Framer.</li>
            <li>
              Go to <strong>Settings → Plugins → API</strong> and create a key for this project.
            </li>
            <li>Copy it once — Framer will not show it again.</li>
            <li>Paste it here. You can revoke it in Framer at any time.</li>
          </ol>
          <p className="subtle" style={{ marginTop: 12 }}>
            The key is encrypted with AES-256-GCM before it touches our database, and is decrypted
            only while a job of yours is running.
          </p>
        </Card>
      </div>
    </>
  )
}
